import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useSearchParams } from "react-router-dom";
import { useTran } from "@/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth";
import { useSurveyTakePageStore, useSurveyTakeStore } from "../hooks";
import SurveyTake from "../components/SurveyTake";

const SurveyTakePage = () => {
  const { t } = useTran();
  const { surveyType, surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const takePageStore = useSurveyTakePageStore();
  const takeStore = useSurveyTakeStore();

  const token = searchParams.get("token") || "";
  const inviteEmail = (searchParams.get("email") || "").trim();
  const inviteName = (searchParams.get("name") || "").trim();
  const inviteOrg = (searchParams.get("org") || "").trim();

  const isAuthenticated = Boolean(user);

  useEffect(() => {
    takePageStore.setParams({
      surveyType,
      surveyId,
      token,
      inviteEmail,
      inviteName,
      inviteOrg,
      isAuthenticated,
    });
    takePageStore.loadSurvey(t);
  }, [
    inviteEmail,
    inviteName,
    inviteOrg,
    isAuthenticated,
    surveyId,
    surveyType,
    t,
    takePageStore,
    token,
  ]);
  const isDisabled = takePageStore.guestInfo.email && isAuthenticated;
  const viewMode = takeStore.viewMode;

  if (takePageStore.error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t("Survey link unavailable")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {takePageStore.error}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 space-y-6">
      {takePageStore.loading ? (
        <Skeleton className="h-32 w-full max-w-2xl" />
      ) : null}

      {!isAuthenticated && viewMode !== "result" ? (
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>{t("Tell us about you")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-2 md:grid-cols-2">
            <FormField
              label={t("Full name")}
              value={takePageStore.guestInfo.name}
              onChange={(event) =>
                takePageStore.setGuestField("name", event.target.value)
              }
              placeholder={t("Enter full name")}
            />
            <FormField
              label={t("Email")}
              type="email"
              value={takePageStore.guestInfo.email}
              readOnly={!isDisabled}
              disabled={!isDisabled}
              onChange={(event) =>
                takePageStore.setGuestField("email", event.target.value)
              }
              placeholder={t("Enter email")}
            />
            <FormField
              className="md:col-span-2"
              label={t("Organization")}
              value={takePageStore.guestInfo.organization}
              onChange={(event) =>
                takePageStore.setGuestField("organization", event.target.value)
              }
              placeholder={t("Enter organization")}
            />
            {!takePageStore.guestInfoValid ? (
              <p className="text-xs text-destructive md:col-span-3">
                {t("Please complete your details before submitting.")}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {!takePageStore.loading ? (
        <div className="w-full max-w-2xl">
          <SurveyTake
            survey={takePageStore.survey}
            shareToken={token}
            externalRespondent={
              isAuthenticated
                ? undefined
                : takePageStore.guestInfoValid
                  ? takePageStore.guestInfo
                  : undefined
            }
            disableSubmit={!takePageStore.guestInfoValid}
          />
        </div>
      ) : null}
    </div>
  );
};

export default observer(SurveyTakePage);
